import { useEffect, useState, useMemo, useCallback } from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { NavBar } from 'shop/components'
import {
  UpdateDateCartRecipientDetails,
  getPayNextStep
} from 'shop/components/Checkout/Network'
import {
  findAndSetCartErrors,
  findAndSetDeliveryErrors,
  getCheckoutCtaValues,
  getCurrentDiscountWarningErrors,
  getInvalidItemIds,
  isDiscountErrorCode,
  DiscountErrorCode,
  isAddressParamsValid,
  defaultCheckoutErrorMessage
} from 'shop/components/Checkout/utils'
import { strippedContactNumPrefix } from 'shop/components/Inputs/utils'
import { FORM_DEFAULTS, FormFields } from 'shop/components/Checkout/types'
import {
  useShop,
  useConsumerCart,
  useModal,
  useCheckout,
  useMediaQueries,
  useIntersectionObserver
} from 'shop/hooks'
import { useForm } from 'react-hook-form'
import { UseFormReturn } from 'react-hook-form'
import { usePageViewTracker } from 'tracker'
import { useNavigate } from 'react-router-dom'
import TippingV2 from 'shop/components/Tipping/TippingV2'
import { returnToShop } from 'shop/utils/common'
import { Cart } from 'shop/components/Cart'
import { StyledHTMLElement } from 'shop/theme/types'
import {
  CartMutationResponse,
  CartProductError,
  OrderItemWithVatPrices,
  OrderItem,
  OrderItemWithLimit,
  TipValues,
  ConsumerCart
} from 'shop/types/cart'
import {
  trackDeniedDiscountCodeConsumerCart,
  trackRemoveDiscountCodeConsumerCart
} from 'shop/components/Checkout/tracking/helpers'
import {
  findErrorWarning,
  getInvalidItems,
  isCartProductError
} from 'shop/utils'
import { PAGE_CHECKOUT } from 'shop/components/NavBar/utils'
import CheckoutFormComponent from 'shop/components/Checkout/CheckoutForm'
import CheckoutErrorModals from 'shop/components/Checkout/CheckoutErrorModals'
import { CartType } from 'shop/components/Cart/Cart'
import { DELIVERY_FULFILLMENT, FulfillmentType } from 'shop/types'

// Determines whether error modals trigger checkout page form submission ("PAY") or not ('PAGE")
type ErrorModalType = 'PAGE' | 'PAY' | null

const Checkout = () => {
  const { useShopClient } = useShop()
  const { isMobile } = useMediaQueries()
  const client = useShopClient()
  const navigate = useNavigate()
  const { openModal } = useModal()
  // Track page view
  usePageViewTracker()

  const {
    cart: consumerCart,
    updateConsumerCart,
    updateFulfillmentConsumerCart,
    removeDiscountConsumerCart,
    cartLoading,
    errors: consumerCartErrors,
    warnings: consumerCartWarnings,
    removeOrderItemsConsumerCart
  } = useConsumerCart()
  const {
    timeSlotValidation,
    dynamicPrepTimeSlotValidation,
    discountErrorWarnings,
    rewardsRef,
    productError,
    setIsPaying,
    isTippingOpen,
    setIsTippingOpen,
    updateExceededLimitItems,
    invalidFulfillmentTypeError,
    isFulfillmentToggleTriggered,
    setIsFulfillmentToggleTriggered,
    handleFulfillmentTypeChange
  } = useCheckout()

  const [showProductErrorModal, setShowProductErrorModal] =
    useState<ErrorModalType>(null)
  const [invalidItems, setInvalidItems] = useState<
    (OrderItem | OrderItemWithVatPrices)[]
  >([])
  const [showDiscountErrorModal, setShowDiscountErrorModal] =
    useState<ErrorModalType>(null)
  const [paymentError, setPaymentError] = useState<Error | null>(null)
  const [tippingValues, setTippingValues] = useState<TipValues[]>([])
  const [hasSeenRewards, setHasSeenRewards] = useState<boolean>(false)
  const [isDeliveryAddressFormOpen, setIsDeliveryAddressFormOpen] =
    useState<boolean>(false)
  const [pendingFulfillmentTypeChange, setPendingFulfillmentTypeChange] =
    useState<FulfillmentType | null>(null)
  /** Holds the updated cart after successful fulfillment type change for checkout form  reinitialization in CheckoutForm component */
  const [updatedCartForFormReinit, setUpdatedCartForFormReinit] =
    useState<ConsumerCart | null>(null)

  const checkoutForm = useForm({
    mode: 'onBlur',
    defaultValues: FORM_DEFAULTS
  })

  const {
    setError: setFormError,
    clearErrors,
    formState: { errors }
  }: UseFormReturn<FormFields> = checkoutForm

  const fulfillmentType = consumerCart?.fulfillment?.type

  const activeTimeSlotValidation =
    timeSlotValidation || dynamicPrepTimeSlotValidation

  // Check for subtotal discount - MOV
  const hasSubtotalDiscount = useMemo(() => {
    // FIXME: use consumerCart?.summary.subtotal.reduction instead
    const hasSubtotalReduction =
      consumerCart?.summary.subtotal.discounted !==
      consumerCart?.summary.subtotal.base
    const hasDiscount = !!consumerCart?.summary.discount
    return !!(hasDiscount && hasSubtotalReduction)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerCart?.summary.subtotal.discounted])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (timeSlotValidation || dynamicPrepTimeSlotValidation) {
      openModal('timeslot')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSlotValidation, dynamicPrepTimeSlotValidation])

  /** Get tip values first time cart is loaded */
  useEffect(() => {
    if (!consumerCart?.id) return
    if (!!consumerCart.tipValues?.length) {
      setTippingValues(consumerCart.tipValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerCart?.id])

  /** Handles consumer delivery address & cart validations. Sets appropriate UI error messages */
  useEffect(() => {
    const hasValidationInfo =
      !!consumerCartErrors.length || !!consumerCartWarnings.length

    if (!hasValidationInfo) {
      // TODO: Fix this - React Hook Form v7 is type-restricted to only accept field names that are defined in your FormFields type. However, "address_api" and "checkout_cart" are custom error keys that don't exist in the form schema.
      ;(clearErrors as any)('address_api')
      ;(clearErrors as any)('checkout_cart')
      return
    }

    // If there is a discount error and a discount code is already applied, show discount error modal
    if (consumerCartErrors.length && consumerCart?.summary.discount?.code) {
      const discountErrorCode = findErrorWarning(
        consumerCartErrors,
        isDiscountErrorCode
      )?.message as DiscountErrorCode
      // Trigger discount errors.
      if (discountErrorCode) {
        // If showDiscountErrorModal is set to 'PAY' do not override
        !showDiscountErrorModal && setShowDiscountErrorModal('PAGE')
      }
    }

    if (consumerCartErrors.length) {
      const currentProductError = findErrorWarning(
        consumerCartErrors,
        isCartProductError
      ) as CartProductError

      if (currentProductError && consumerCart?.orderItems) {
        setInvalidItems(
          getInvalidItems(currentProductError, consumerCart.orderItems)
        )
        // If showProductErrorModal is set to 'PAY' do not override
        !showProductErrorModal && setShowProductErrorModal('PAGE')
      }
    }

    // Find and handle cart error code from cart errors
    findAndSetCartErrors(
      consumerCartErrors,
      consumerCartWarnings,
      hasSubtotalDiscount,
      setFormError,
      clearErrors
    )
    // Find and handle delivery error code from cart errors
    const deliveryErrorCode = findAndSetDeliveryErrors(
      consumerCartErrors,
      consumerCartWarnings,
      setFormError,
      clearErrors
    )
    if (deliveryErrorCode) setIsDeliveryAddressFormOpen(true)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerCartWarnings, consumerCartErrors])

  /** REWARDS */
  // If rewards have come into viewport, set hasSeenRewards to true
  const handleRewardsIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setHasSeenRewards(true)
      }
    })
  }

  // Create intersectionObs to check if rewards have been scrolled 50% up the viewport
  const rewardsObserver = useIntersectionObserver(
    handleRewardsIntersection,
    rewardsRef.current,
    {
      root: null,
      threshold: 0,
      rootMargin: '0px 0px -50% 0px'
    }
  )

  useEffect(() => {
    // Stop checking if rewards are in viewport once user has seen them or clicked CTA
    if (hasSeenRewards && rewardsObserver) {
      rewardsObserver.disconnect()
    }
  }, [hasSeenRewards, rewardsObserver])

  /** Retries the pending fulfillment change after error resolution */
  const retryFulfillmentChange = (proceedWithNewPrices?: boolean) => {
    if (!pendingFulfillmentTypeChange) return

    handleFulfillmentTypeChange(
      pendingFulfillmentTypeChange,
      proceedWithNewPrices
    ).then((retryRes) => {
      const { cart: updatedCart, errors } = retryRes || {}

      // If there are still errors, let the modal handle them again
      if (errors?.length) return

      // On success reinitialize form & clear pending state
      if (updatedCart) {
        setUpdatedCartForFormReinit(updatedCart)
        setPendingFulfillmentTypeChange(null)
        setIsFulfillmentToggleTriggered(false)
      }
    })
  }

  /** Removes order items when there is a product error */
  const handleRemoveOrderItemsFromCart = useCallback(
    (submitForm: boolean) => {
      const invalidItemIds = getInvalidItemIds(invalidItems)

      removeOrderItemsConsumerCart({ ids: invalidItemIds }).then(
        (res: CartMutationResponse | null) => {
          // If cart exists but has no order items after removal, redirect user back to shop
          if (res?.cart && !res?.cart?.orderItems.length) {
            setShowProductErrorModal(null)
            setPendingFulfillmentTypeChange(null)
            returnToShop(consumerCart?.store.slug, navigate)
            return
          }

          setShowProductErrorModal(null)

          // If this was triggered by fulfillment toggle, retry the fulfillment change
          if (pendingFulfillmentTypeChange) {
            retryFulfillmentChange()
            return
          }

          // Otherwise proceed with form submission if requested
          if (submitForm) {
            handleSubmitForm()
          }
        }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      productError,
      invalidItems,
      pendingFulfillmentTypeChange,
      handleFulfillmentTypeChange
    ]
  )

  /** Send boolean to BE confirming user has agreed price change */
  const handleProceedWithPriceChange = useCallback(
    (submitForm: boolean) => {
      // If error triggered by fulfillment toggle attempt, retry with proceedWithNewPrices flag
      if (pendingFulfillmentTypeChange) {
        setShowProductErrorModal(null)
        retryFulfillmentChange(true)
        return
      }

      // Otherwise just update cart with new prices (regular checkout flow)
      updateFulfillmentConsumerCart({ proceedWithNewPrices: true })
        .then(() => {
          setShowProductErrorModal(null)

          // Proceed with form submission if requested
          if (submitForm) {
            handleSubmitForm()
          }
        })
        .catch((error) => {
          console.error('Error proceeding with price change:', error)
          setShowProductErrorModal(null)
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingFulfillmentTypeChange, handleFulfillmentTypeChange]
  )

  /** Updates order item quantities when they exceed product limits */
  const handleUpdateOrderItems = (submitForm: boolean) => {
    const limitedItems = invalidItems as OrderItemWithLimit[]
    updateExceededLimitItems(limitedItems).then(() => {
      setShowProductErrorModal(null)

      // If this was triggered by fulfillment toggle, retry the fulfillment change
      if (pendingFulfillmentTypeChange) {
        retryFulfillmentChange()
        return
      }

      // Otherwise proceed with form submission if requested
      if (submitForm) {
        handleSubmitForm()
      }
    })
  }

  if (!consumerCart?.summary || !fulfillmentType) return <></>

  const {
    orderItems,
    summary,
    store,
    loyaltyCards,
    orderNotes,
    additionalItems,
    potentialLoyaltyGains
  } = consumerCart

  const { checkoutPayCtaActive } = getCheckoutCtaValues(
    loyaltyCards,
    isMobile,
    hasSeenRewards
  )

  const setTipOnConsumerCart = (tipAmount?: string) => {
    if (!!tipAmount) sessionStorage.setItem('hasSelectedTip', 'true')
    if (isTippingOpen) setIsTippingOpen(false)
    updateConsumerCart({
      tipValue: Number(tipAmount)
    }).then((cartResponse: CartMutationResponse | null) => {
      const { cart } = cartResponse || {}
      if (cart) {
        return
      }
      throw new Error('Update cart failed')
    })
  }

  /** Updates cart when user clicks pay & navigates to payment page */
  const checkout = (tipAmount?: string) => {
    if (!!tipAmount) sessionStorage.setItem('hasSelectedTip', 'true')
    if (isTippingOpen) setIsTippingOpen(false)

    const {
      customerDetails: {
        contactNumPrefix,
        contactNumber,
        ...otherCustomerDetails
      },
      recipientDetails,
      forSomeoneElse,
      orderNotes,
      orderNotesEnabled,
      dropoffNotes,
      marketingOptIn,
      saveAddress,
      customFieldValue
    } = checkoutForm.getValues()

    setIsPaying(true)

    const customerDetails = {
      ...otherCustomerDetails,
      contactNumber: `${strippedContactNumPrefix(
        contactNumPrefix
      )}${contactNumber}`
    }

    let amendedRecipientDetails: UpdateDateCartRecipientDetails =
      recipientDetails
    if (recipientDetails?.firstName) {
      const {
        contactNumPrefix: rec_prefix,
        contactNumber: rec_num,
        ...otherRecipientDetails
      } = recipientDetails

      amendedRecipientDetails = {
        ...otherRecipientDetails,
        contactNumber: `${strippedContactNumPrefix(rec_prefix)}${rec_num}`
      }
    }

    // Ensure customer_details.email is in lowercase
    if (customerDetails?.email) {
      customerDetails.email = customerDetails.email.toLowerCase()
    }

    return updateConsumerCart(
      {
        ...(customFieldValue ? { customFieldValue } : {}),
        customerDetails,
        ...(orderNotesEnabled ? { orderNotes } : {}),
        ...(forSomeoneElse
          ? { recipientDetails: amendedRecipientDetails || recipientDetails }
          : {}),
        ...(dropoffNotes ? { dropoffNotes } : {}),
        marketingOptIn,
        saveAddress,
        tipValue: !!tipAmount ? Number(tipAmount) : undefined
      },
      { skipLoading: true }
    )
      .then((cartResponse: CartMutationResponse | null) => {
        if (cartResponse) {
          const { cart, warnings, errors } = cartResponse

          // Validation handling
          if (errors.length || warnings.length) {
            // Get most recent discount errors/warnings
            const currentDiscountErrorWarnings =
              getCurrentDiscountWarningErrors(errors, warnings)

            const currentProductError = findErrorWarning(
              errors,
              isCartProductError
            ) as CartProductError

            // Trigger discount errors/warnings
            if (currentDiscountErrorWarnings.length) {
              setShowDiscountErrorModal('PAY')
              setIsPaying(false)
              return
            }

            // If there are product errors, show product error modal
            if (currentProductError) {
              setShowProductErrorModal('PAY')
              setIsPaying(false)
              return
            }

            // Block users with generic error if there are errors or warnings that we are not handling
            setPaymentError(
              new Error(
                'Unable to process payments at this time. Please try again later.'
              )
            )
            setIsPaying(false)
            return
          }

          if (cart) {
            return getPayNextStep(client, consumerCart.id).finally(() =>
              setIsPaying(false)
            )
          }
        }
        throw new Error('Update cart failed')
      })
      .then(async (result) => {
        if (result) {
          if (result.data.payForCart.nextStep === 'skip') {
            const transactionId = result.data.payForCart.transactionId
            return navigate(`/track/${transactionId}`)
          }

          return navigate('/pay')
        }
      })
      .catch((error) => {
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          switch (error.graphQLErrors[0].code) {
            case 'STRIPE_AMOUNT_TOO_SMALL':
              return setPaymentError(
                new Error('Please ensure your total is above £0.30')
              )
            default:
              return setPaymentError(
                new Error(
                  'Unable to process payments at this time. Please try again later.'
                )
              )
          }
        }

        setPaymentError(
          new Error(
            'Unable to process payments at this time. Please try again later.'
          )
        )
        return setIsPaying(false)
      })
  }

  const hasTippingValues = !!tippingValues?.length
  const hasUserSelectedTipBefore =
    !!sessionStorage.getItem('hasSelectedTip') ||
    Number(consumerCart.summary.tip) > 0
  const showTippingModal = isTippingOpen && hasTippingValues

  // TODO: Move this to utils and pass in args
  const scrollToRewards = () => {
    if (rewardsRef.current) {
      const topPosition =
        rewardsRef.current.getBoundingClientRect().top + window.scrollY - 50
      window.scrollTo({ top: topPosition, behavior: 'smooth' })
    }
  }

  /** Scrolls to rewards area if mobile user & has rewards, else normal payment flow */
  const handleSubmitForm = checkoutForm.handleSubmit(() => {
    if (checkoutPayCtaActive) {
      if (hasTippingValues) {
        if (!hasUserSelectedTipBefore) {
          setIsTippingOpen(true)
          return
        }
        checkout(
          consumerCart.summary.tip ? consumerCart.summary.tip : undefined
        )
      } else {
        checkout()
      }
    } else {
      scrollToRewards()
    }
  })

  const handleFulfillmentToggleClick = (
    projectedFulfillmentType: FulfillmentType
  ) => {
    if (cartLoading || projectedFulfillmentType === fulfillmentType) return

    // Set pending fulfillment type change to track this operation in case of errors
    setPendingFulfillmentTypeChange(projectedFulfillmentType)
    // Sync form delivery address values to BE if it passes FE input validations
    const currentAddress = checkoutForm.getValues('deliveryAddress')
    const hasValidAddress = isAddressParamsValid(currentAddress)
    const sendDeliveryAddress =
      hasValidAddress && projectedFulfillmentType === DELIVERY_FULFILLMENT
    handleFulfillmentTypeChange(
      projectedFulfillmentType,
      undefined,
      sendDeliveryAddress ? currentAddress : undefined
    )
      .then((res) => {
        const { cart: updatedCart, errors } = res || {}

        // If errors, let error modal take over
        // The error handlers will retry the fulfillment change after resolving errors
        if (errors?.length) {
          return
        }

        // On success reinitialize form & clear pending state
        if (updatedCart) {
          setUpdatedCartForFormReinit(updatedCart)
          setPendingFulfillmentTypeChange(null)
          setIsFulfillmentToggleTriggered(false)
        }
      })
      .catch(() => {
        // TODO: Fix this - React Hook Form v7 is type-restricted to only accept field names that are defined in your FormFields type. However, "address_api" and "checkout_cart" are custom error keys that don't exist in the form schema.
        ;(setFormError as any)('address_api', {
          type: 'manual',
          message: defaultCheckoutErrorMessage
        })
        setIsDeliveryAddressFormOpen(true)
        setIsFulfillmentToggleTriggered(false)
      })
  }

  // TODO: Move this into CheckoutErrorModals?
  const handleRemoveDiscountViaModal = (submitForm: boolean) => {
    const discountCode = summary.discount?.code as string
    removeDiscountConsumerCart()
      .then(() => {
        trackRemoveDiscountCodeConsumerCart(discountCode)
        // reset discount error modal state to default
        setShowDiscountErrorModal(null)

        // If this was triggered by fulfillment toggle, retry the fulfillment change
        if (pendingFulfillmentTypeChange) {
          retryFulfillmentChange()
          return
        }

        // If user was trying to pay, manually retrigger pay button flow after discount removal
        if (submitForm) {
          handleSubmitForm()
        }
      })
      .catch(() => {
        trackDeniedDiscountCodeConsumerCart(discountCode)
      })
  }

  return (
    <>
      <CheckoutErrorModals
        showProductErrorModal={showProductErrorModal}
        showDiscountErrorModal={showDiscountErrorModal}
        productError={productError}
        invalidItems={invalidItems}
        invalidFulfillmentTypeError={invalidFulfillmentTypeError}
        handleProceedWithPriceChange={handleProceedWithPriceChange}
        handleRemoveOrderItemsFromCart={handleRemoveOrderItemsFromCart}
        handleRemoveDiscountViaModal={handleRemoveDiscountViaModal}
        handleUpdateOrderItems={handleUpdateOrderItems}
        activeTimeSlotValidation={activeTimeSlotValidation}
        discountErrorWarnings={discountErrorWarnings}
        hasSubtotalDiscount={hasSubtotalDiscount}
        isFulfillmentToggleTriggered={isFulfillmentToggleTriggered}
        formErrors={errors}
      />
      {showTippingModal && (
        <TippingV2
          tippingValues={tippingValues}
          handleSubmitTip={
            hasUserSelectedTipBefore ? setTipOnConsumerCart : checkout
          }
          currentTipValue={consumerCart.summary.tip}
        />
      )}
      <Container>
        <NavBar page={PAGE_CHECKOUT} storeSlug={store.slug} />
        <ContentWrapper id='checkout-form-wrapper'>
          <CheckoutFormComponent
            checkout={checkout}
            checkoutForm={checkoutForm}
            handleSubmitForm={handleSubmitForm}
            isDeliveryAddressFormOpen={isDeliveryAddressFormOpen}
            setIsDeliveryAddressFormOpen={setIsDeliveryAddressFormOpen}
            hasSeenRewards={hasSeenRewards}
            hasTippingValues={hasTippingValues}
            paymentError={paymentError}
            handleFulfillmentToggleClick={handleFulfillmentToggleClick}
            updatedCartForFormReinit={updatedCartForFormReinit}
            setUpdatedCartForFormReinit={setUpdatedCartForFormReinit}
          />
          <CartContainer>
            <Cart
              type={CartType.Summary}
              orderItems={orderItems}
              summary={summary}
              orderNotes={orderNotes}
              loyaltyCards={loyaltyCards}
              fulfillmentType={fulfillmentType}
              additionalItems={additionalItems}
              potentialLoyaltyGains={potentialLoyaltyGains}
              isEditable={true}
              isCartLoading={cartLoading}
            />
          </CartContainer>
        </ContentWrapper>
      </Container>
    </>
  )
}

const Container = styled.div(
  (): CSSObject => ({
    minHeight: '100vh',
    backgroundColor: '#F5F5F5',
    position: 'relative'
  })
)

const ContentWrapper = styled.div<StyledHTMLElement>(({ theme }) => ({
  minHeight: 'min-content',
  position: 'relative',
  margin: '0 auto',
  width: '100%',
  maxWidth: '1470px',
  height: '100%',
  display: 'grid',
  gap: '12px',
  paddingBottom: '90px',
  flexDirection: 'column-reverse',
  [theme.mediaQueries.viewport4]: {
    padding: '40px',
    gap: '24px'
  },
  [theme.mediaQueries.viewport8]: {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    padding: '40px 75px 90px',
    gap: '40px',
    justifyContent: 'center'
  }
}))

const CartContainer = styled.div(
  ({ theme }): CSSObject => ({
    height: '100%',
    backgroundColor: 'white',
    padding: '24px 16px',
    minWidth: 0,
    [theme.mediaQueries.viewport4]: {
      borderRadius: theme.borderRadius,
      padding: '32px 32px 12px'
    },
    [theme.mediaQueries.viewport8]: {
      position: 'sticky',
      top: '104px',
      height: 'fit-content'
    }
  })
)

export default Checkout
